import { ArrowUpRight } from '../../../design-system/icons';
import { cn } from '../../../lib/utils';
import { formatTimecode } from '../../../lib/media/timecode';
import { noteAtLabel } from './notes/lib/noteAtLabel';
import { RISK_BG_CLASS, RISK_RULE_CLASS, RISK_TEXT_CLASS } from './riskColour';
import type { ContentSeverity } from './types';

const CHIP_ARROW_SIZE = 11;

const ROW_CLASS =
  'group relative flex w-full items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-body-sm-regular';
const INTERACTIVE_ROW_CLASS =
  'transition-colors hover:border-input hover:bg-muted focus-visible:border-input focus-visible:bg-muted';
const RULE_CLASS = 'absolute inset-y-2 left-0 w-0.5 rounded-full';
const SEVERITY_PILL_CLASS = 'shrink-0 rounded-full px-2 py-0.5 text-caption-medium font-semibold uppercase tracking-wide';
const TIMECODE_CLASS = 'shrink-0 text-caption-regular tabular-nums text-muted-foreground';
const LABEL_CLASS = 'min-w-0 flex-1 text-foreground';
const NOTE_AT_CLASS =
  'ml-auto hidden shrink-0 items-center gap-1 text-caption-medium font-semibold text-foreground group-hover:inline-flex group-focus-visible:inline-flex';

export type AiFlagChipProps = {
  /** Severity word the AI attached to this flag ("Critical"/"High"/"Medium"/
   *  "Low", or any other free-form word it emitted — rendered verbatim). */
  severity: ContentSeverity;
  /** The flag's text, with the "SEVERITY @ TIME:" prefix already stripped. */
  label: string;
  /** The flag's parsed moment, in milliseconds. */
  tMs: number;
  /**
   * Jump the player + notes composer to `tMs` (`useNotesSession.pinToTime`
   * owns the actual seek/attach/toast — this component only asks for it).
   * Omitted on surfaces with no notes session (read-only / non-notes mode):
   * the chip then renders as a plain, non-interactive row — still readable,
   * but without a click affordance that would do nothing.
   */
  onNoteAt?: (tMs: number) => void;
};

type FlagContentProps = Pick<AiFlagChipProps, 'severity' | 'label' | 'tMs'>;

function noteAtFlagLabel(tMs: number, label: string): string {
  return `${noteAtLabel(tMs)}: ${label}`;
}

/** Every flag reads as its severity, its moment and its text — with or without the click affordance. */
function FlagContent({ severity, label, tMs }: FlagContentProps) {
  return (
    <>
      <span aria-hidden className={cn(RULE_CLASS, RISK_RULE_CLASS)} />
      <span className={cn(SEVERITY_PILL_CLASS, RISK_BG_CLASS, RISK_TEXT_CLASS)}>{severity}</span>
      <span className={TIMECODE_CLASS}>{formatTimecode(tMs)}</span>
      <span className={LABEL_CLASS}>{label}</span>
    </>
  );
}

/**
 * One timed AI risk flag: a full-width bordered row with a red rule, a
 * severity pill, the flag's moment and its text. With `onNoteAt` the row is a
 * button whose "Note at 0:56" affordance appears on hover/focus. Untimed flags
 * keep the plain pill instead (see ContentResultTabs.tsx).
 */
export function AiFlagChip({ severity, label, tMs, onNoteAt }: AiFlagChipProps) {
  if (!onNoteAt) {
    return (
      <div className={ROW_CLASS}>
        <FlagContent severity={severity} label={label} tMs={tMs} />
      </div>
    );
  }

  const noteAt = () => onNoteAt(tMs);
  return (
    <button type="button" onClick={noteAt} aria-label={noteAtFlagLabel(tMs, label)} className={cn(ROW_CLASS, INTERACTIVE_ROW_CLASS)}>
      <FlagContent severity={severity} label={label} tMs={tMs} />
      <span aria-hidden className={NOTE_AT_CLASS}>
        {noteAtLabel(tMs)}
        <ArrowUpRight size={CHIP_ARROW_SIZE} />
      </span>
    </button>
  );
}
